import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { 
  RefreshCw, CheckCircle, XCircle, Upload, Download, 
  Car, AlertTriangle, Plus, Edit, Trash2, Eye, 
  Server, Shield, ToggleLeft, ToggleRight, FileText
} from 'lucide-react';

const DealerCenterManagement = () => {
  const [status, setStatus] = useState(null);
  const [settings, setSettings] = useState(null);
  const [siteSettings, setSiteSettings] = useState(null);
  const [preview, setPreview] = useState(null);
  const [syncLogs, setSyncLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [statusRes, settingsRes, siteSettingsRes, logsRes] = await Promise.all([
        axios.get(`${API}/admin/dealercenter/status`, { headers }),
        axios.get(`${API}/admin/dealercenter/settings`, { headers }),
        axios.get(`${API}/site-settings`),
        axios.get(`${API}/admin/dealercenter/sync-logs`, { headers }).catch(() => ({ data: [] }))
      ]);
      
      setStatus(statusRes.data);
      setSettings(settingsRes.data);
      setSiteSettings(siteSettingsRes.data);
      setSyncLogs(logsRes.data || []);
    } catch (error) {
      console.error('Error fetching DealerCenter data:', error);
      toast.error('Failed to fetch DealerCenter status');
    } finally {
      setLoading(false);
    }
  };

  const fetchPreview = async () => {
    setPreviewing(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/admin/dealercenter/preview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreview(response.data);
      if (response.data.success) {
        toast.success('Preview loaded successfully');
      }
    } catch (error) {
      console.error('Error fetching preview:', error);
      toast.error('Failed to load preview');
    } finally {
      setPreviewing(false);
    }
  };

  const runImport = async () => {
    setImporting(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(`${API}/admin/dealercenter/import`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success('Import started in background! Check sync logs for progress.');
        setPreview(null);
        // Poll sync logs for a bit to show progress
        setTimeout(() => fetchAll(), 5000);
        setTimeout(() => fetchAll(), 15000);
        setTimeout(() => fetchAll(), 30000);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Error importing:', error);
      toast.error('Import failed');
    } finally {
      setImporting(false);
    }
  };

  const toggleAutoImport = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const newValue = !siteSettings?.dealercenter_auto_sync;
      
      // Update site settings
      await axios.put(`${API}/admin/site-settings`, 
        { ...siteSettings, dealercenter_auto_sync: newValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSiteSettings(prev => ({ ...prev, dealercenter_auto_sync: newValue }));
      toast.success(`Daily auto-sync ${newValue ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-[#ff6b35]" size={32} />
      </div>
    );
  }

  return (
    <div data-testid="dealercenter-management">
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Server className="text-[#ff6b35]" />
            DealerCenter Integration
          </h1>
          <p className="text-[#a8b8d0] text-sm mt-1">
            Sync inventory from DealerCenter FTP feed
          </p>
        </div>
        <button onClick={fetchAll} className="btn-secondary flex items-center gap-2">
          <RefreshCw size={18} />
          Refresh
        </button>
      </div>

      {/* Connection Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-[#132238] rounded-xl p-6 border border-[#1a2d47]">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Shield className="text-[#22c55e]" size={20} />
            Connection Status
          </h3>
          
          {status?.connected ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-[#22c55e]">
                <CheckCircle size={20} />
                <span className="font-medium">Connected</span>
              </div>
              <div className="text-sm text-[#a8b8d0] space-y-1">
                <p><span className="text-white">Host:</span> {status.host}</p>
                <p><span className="text-white">Protocol:</span> {status.connection_type}</p>
                <p><span className="text-white">Files found:</span> {status.dealercenter_files?.length || 0} DealerCenter file(s)</p>
              </div>
              
              {status.dealercenter_files?.length > 0 && (
                <div className="mt-4 p-3 bg-[#1a2d47]/50 rounded-lg">
                  <p className="text-xs text-[#a8b8d0] mb-1">Latest file:</p>
                  <div className="flex items-center gap-2 text-sm text-white">
                    <FileText size={14} className="text-[#ff6b35]" />
                    {status.dealercenter_files[status.dealercenter_files.length - 1]}
                  </div>
                </div>
              )}
              
              {status.dealercenter_files?.length === 0 && (
                <div className="mt-4 p-3 bg-[#ffc107]/10 border border-[#ffc107]/30 rounded-lg">
                  <p className="text-[#ffc107] text-sm flex items-center gap-2">
                    <AlertTriangle size={16} />
                    Waiting for DealerCenter to upload files
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2 text-red-400">
              <XCircle size={20} />
              <span>Not connected: {status?.error}</span>
            </div>
          )}
        </div>

        {/* Auto Import Settings */}
        <div className="bg-[#132238] rounded-xl p-6 border border-[#1a2d47]">
          <h3 className="text-lg font-semibold text-white mb-4">Import Settings</h3>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-[#1a2d47]/50 rounded-lg">
              <div>
                <p className="text-white font-medium">Daily Auto-Sync</p>
                <p className="text-[#a8b8d0] text-sm">Automatically sync inventory once per day</p>
              </div>
              <button
                onClick={toggleAutoImport}
                className={`p-2 rounded-lg transition-colors ${
                  siteSettings?.dealercenter_auto_sync 
                    ? 'bg-[#22c55e] text-white' 
                    : 'bg-[#1a2d47] text-[#a8b8d0]'
                }`}
                data-testid="auto-sync-toggle"
              >
                {siteSettings?.dealercenter_auto_sync ? (
                  <ToggleRight size={28} />
                ) : (
                  <ToggleLeft size={28} />
                )}
              </button>
            </div>
            
            {/* Sync Logs */}
            {syncLogs.length > 0 && (
              <div className="mt-4">
                <p className="text-white font-medium mb-2">Recent Sync Logs</p>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {syncLogs.slice(0, 5).map((log, idx) => (
                    <div key={idx} className="text-sm text-[#a8b8d0] p-2 bg-[#1a2d47]/30 rounded">
                      <p className="text-white">{new Date(log.timestamp).toLocaleString()}</p>
                      <p>Imported: {log.imported} | Updated: {log.updated} | File: {log.file}</p>
                      {log.errors?.length > 0 && (
                        <p className="text-red-400">Errors: {log.errors.length}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="bg-[#132238] rounded-xl p-6 border border-[#1a2d47] mb-6">
        <h3 className="text-lg font-semibold text-white mb-4">Manual Import</h3>
        
        <div className="flex flex-wrap gap-4">
          <button
            onClick={fetchPreview}
            disabled={previewing || !status?.connected || status?.dealercenter_files?.length === 0}
            className="btn-primary flex items-center gap-2 disabled:opacity-50"
          >
            {previewing ? <RefreshCw className="animate-spin" size={18} /> : <Eye size={18} />}
            Preview Changes
          </button>
          
          <button
            onClick={runImport}
            disabled={importing || !status?.connected || status?.dealercenter_files?.length === 0}
            className="btn-accent flex items-center gap-2 disabled:opacity-50"
          >
            {importing ? <RefreshCw className="animate-spin" size={18} /> : <Download size={18} />}
            Import Now
          </button>
        </div>
        
        {!status?.connected && (
          <p className="text-red-400 text-sm mt-3">Connect to FTP first to enable import</p>
        )}
        
        {status?.connected && status?.dealercenter_files?.length === 0 && (
          <p className="text-[#ffc107] text-sm mt-3">
            No DealerCenter files available yet. Waiting for DealerCenter to send data.
          </p>
        )}
      </div>

      {/* Preview Results */}
      {preview && preview.success && (
        <div className="bg-[#132238] rounded-xl p-6 border border-[#1a2d47]">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Eye className="text-[#ff6b35]" />
            Import Preview - {preview.file}
          </h3>
          
          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
            <div className="bg-[#22c55e]/20 border border-[#22c55e] rounded-lg p-4 text-center">
              <Plus className="mx-auto text-[#22c55e] mb-2" size={24} />
              <p className="text-2xl font-bold text-[#22c55e]">{preview.summary.new_count}</p>
              <p className="text-sm text-[#a8b8d0]">New Vehicles</p>
            </div>
            
            <div className="bg-[#3b82f6]/20 border border-[#3b82f6] rounded-lg p-4 text-center">
              <Edit className="mx-auto text-[#3b82f6] mb-2" size={24} />
              <p className="text-2xl font-bold text-[#3b82f6]">{preview.summary.updated_count}</p>
              <p className="text-sm text-[#a8b8d0]">To Update</p>
            </div>
            
            <div className="bg-[#ffc107]/20 border border-[#ffc107] rounded-lg p-4 text-center">
              <AlertTriangle className="mx-auto text-[#ffc107] mb-2" size={24} />
              <p className="text-2xl font-bold text-[#ffc107]">{preview.summary.missing_count}</p>
              <p className="text-sm text-[#a8b8d0]">Not in Feed</p>
            </div>
            
            <div className="bg-[#1a4d8c]/50 border border-[#1a4d8c] rounded-lg p-4 text-center">
              <Car className="mx-auto text-white mb-2" size={24} />
              <p className="text-2xl font-bold text-white">{preview.summary.total_in_feed}</p>
              <p className="text-sm text-[#a8b8d0]">Total in Feed</p>
            </div>
          </div>
          
          {/* New Vehicles */}
          {preview.new_vehicles.length > 0 && (
            <div className="mb-6">
              <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                <Plus className="text-[#22c55e]" size={18} />
                New Vehicles ({preview.new_vehicles.length})
              </h4>
              <div className="bg-[#1a2d47]/50 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[#1a2d47]">
                    <tr>
                      <th className="text-left text-[#a8b8d0] px-4 py-2">Stock #</th>
                      <th className="text-left text-[#a8b8d0] px-4 py-2">Vehicle</th>
                      <th className="text-left text-[#a8b8d0] px-4 py-2">Price</th>
                      <th className="text-left text-[#a8b8d0] px-4 py-2">Mileage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.new_vehicles.slice(0, 10).map((v, idx) => (
                      <tr key={idx} className="border-t border-[#1a2d47]">
                        <td className="px-4 py-2 text-[#22c55e] font-mono">{v.stock_number}</td>
                        <td className="px-4 py-2 text-white">{v.name} {v.trim && <span className="text-[#a8b8d0]">• {v.trim}</span>}</td>
                        <td className="px-4 py-2 text-white">${v.price?.toLocaleString()}</td>
                        <td className="px-4 py-2 text-[#a8b8d0]">{v.mileage?.toLocaleString()} mi</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {preview.new_vehicles.length > 10 && (
                  <p className="text-[#a8b8d0] text-sm px-4 py-2">...and {preview.new_vehicles.length - 10} more</p>
                )}
              </div>
            </div>
          )}
          
          {/* Updated Vehicles */}
          {preview.updated_vehicles.length > 0 && (
            <div className="mb-6">
              <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                <Edit className="text-[#3b82f6]" size={18} />
                Vehicles to Update ({preview.updated_vehicles.length})
              </h4>
              <div className="bg-[#1a2d47]/50 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[#1a2d47]">
                    <tr>
                      <th className="text-left text-[#a8b8d0] px-4 py-2">Stock #</th>
                      <th className="text-left text-[#a8b8d0] px-4 py-2">Vehicle</th>
                      <th className="text-left text-[#a8b8d0] px-4 py-2">Changes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.updated_vehicles.slice(0, 10).map((v, idx) => (
                      <tr key={idx} className="border-t border-[#1a2d47]">
                        <td className="px-4 py-2 text-[#3b82f6] font-mono">{v.stock_number}</td>
                        <td className="px-4 py-2 text-white">{v.name}</td>
                        <td className="px-4 py-2 text-[#ffc107]">{v.changes.join(', ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {preview.updated_vehicles.length > 10 && (
                  <p className="text-[#a8b8d0] text-sm px-4 py-2">...and {preview.updated_vehicles.length - 10} more</p>
                )}
              </div>
            </div>
          )}
          
          {/* Missing Vehicles */}
          {preview.missing_vehicles.length > 0 && (
            <div>
              <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                <AlertTriangle className="text-[#ffc107]" size={18} />
                Not in Feed ({preview.missing_vehicles.length}) - May be sold
              </h4>
              <div className="bg-[#1a2d47]/50 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[#1a2d47]">
                    <tr>
                      <th className="text-left text-[#a8b8d0] px-4 py-2">Stock #</th>
                      <th className="text-left text-[#a8b8d0] px-4 py-2">Vehicle</th>
                      <th className="text-left text-[#a8b8d0] px-4 py-2">Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.missing_vehicles.slice(0, 10).map((v, idx) => (
                      <tr key={idx} className="border-t border-[#1a2d47]">
                        <td className="px-4 py-2 text-[#ffc107] font-mono">{v.stock_number}</td>
                        <td className="px-4 py-2 text-white">{v.name}</td>
                        <td className="px-4 py-2 text-[#a8b8d0]">{v.note}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {preview.missing_vehicles.length > 10 && (
                  <p className="text-[#a8b8d0] text-sm px-4 py-2">...and {preview.missing_vehicles.length - 10} more</p>
                )}
              </div>
            </div>
          )}
          
          {/* No Changes */}
          {preview.summary.new_count === 0 && preview.summary.updated_count === 0 && preview.summary.missing_count === 0 && (
            <div className="text-center py-8">
              <CheckCircle className="mx-auto text-[#22c55e] mb-3" size={48} />
              <p className="text-white font-medium">Everything is in sync!</p>
              <p className="text-[#a8b8d0] text-sm">No changes detected between feed and inventory</p>
            </div>
          )}
          
          {/* Import Button */}
          {(preview.summary.new_count > 0 || preview.summary.updated_count > 0) && (
            <div className="mt-6 pt-6 border-t border-[#1a2d47] flex justify-end">
              <button
                onClick={runImport}
                disabled={importing}
                className="btn-accent flex items-center gap-2"
              >
                {importing ? <RefreshCw className="animate-spin" size={18} /> : <Download size={18} />}
                Apply Changes ({preview.summary.new_count} new, {preview.summary.updated_count} updates)
              </button>
            </div>
          )}
        </div>
      )}
      
      {/* FTP Credentials Info */}
      <div className="mt-6 bg-[#1a2d47]/30 rounded-xl p-6 border border-[#1a2d47]">
        <h3 className="text-white font-medium mb-3">DealerCenter FTP Credentials</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-[#a8b8d0]">Host</p>
            <p className="text-white font-mono">ftp.drivehq.com</p>
          </div>
          <div>
            <p className="text-[#a8b8d0]">Protocol</p>
            <p className="text-white">SFTP (Port 22) or FTPS (Port 21)</p>
          </div>
          <div>
            <p className="text-[#a8b8d0]">Username</p>
            <p className="text-white font-mono">akogloballlc@gmail.com</p>
          </div>
          <div>
            <p className="text-[#a8b8d0]">File Format</p>
            <p className="text-white">DealerCenter_YYYYMMDD_DCID.csv</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DealerCenterManagement;
